"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Bell, ChevronDown, HelpCircle, LayoutDashboard, LogOut, Wallet } from "lucide-react";
import { useAuth } from "@/lib/auth/auth-context";
import { getUserContact, getUserDisplayName, getUserInitial } from "@/lib/auth/utils";
import { useWalletBalance } from "@/hooks/useWalletBalance";
import { useAppLocale, useT } from "@/i18n/context";

interface UserMenuProps {
  variant?: "light" | "dark";
}

export function UserMenu({ variant = "light" }: UserMenuProps) {
  const router = useRouter();
  const t = useT();
  const locale = useAppLocale();
  const { user, logout } = useAuth();
  const { balance, isPending, isError } = useWalletBalance();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const menuItems = [
    {
      icon: LayoutDashboard,
      label: t("common.userMenu.dashboard.profile", "پروفایل"),
      href: "/dashboard/profile",
    },
    {
      icon: Bell,
      label: t("common.userMenu.notifications", "اعلان‌ها"),
      href: "/dashboard/notifications",
    },
    {
      icon: HelpCircle,
      label: t("common.userMenu.support", "راهنما و پشتیبانی"),
      href: "/dashboard/tickets",
    },
  ];

  const dark = variant === "dark";
  const triggerCls = dark
    ? "flex items-center gap-2 rounded-xl border border-white/10 p-1.5 pl-3 transition-colors hover:bg-white/10"
    : "flex items-center gap-2 rounded-xl border border-grayScale-200 p-1.5 pl-3 transition-colors hover:bg-grayScale-100 dark:border-grayScale-600 dark:hover:bg-grayScale-700";
  const nameCls = dark
    ? "hidden text-sm font-semibold text-white sm:block"
    : "hidden text-sm font-semibold text-grayScale-900 dark:text-white sm:block";

  const displayName = getUserDisplayName(user) || t("common.userMenu.defaultName", "کاربر ترندی");
  const userInitial = getUserInitial(user) || t("common.userMenu.defaultInitial", "ک");
  const userContact = getUserContact(user) || "—";
  const [triggerImgErr, setTriggerImgErr] = useState(false);
  const [dropdownImgErr, setDropdownImgErr] = useState(false);
  const showTriggerImg = !!user?.avatar_url && !triggerImgErr;
  const showDropdownImg = !!user?.avatar_url && !dropdownImgErr;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    logout();
    router.push("/auth");
  };

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => { setOpen(!open); setTriggerImgErr(false); setDropdownImgErr(false); }} className={triggerCls}>
        <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-primary-main to-teal text-xs font-bold text-white">
          {showTriggerImg ? (
            <img src={user!.avatar_url!} alt="" className="h-full w-full object-cover" onError={() => setTriggerImgErr(true)} />
          ) : (
            userInitial
          )}
        </div>
        <span className={nameCls}>{displayName}</span>
        <ChevronDown size={14} className="hidden text-grayScale-400 sm:block" />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-64 origin-top-left animate-in fade-in slide-in-from-top-2 rounded-2xl border border-grayScale-200 bg-white shadow-2xl dark:border-grayScale-700 dark:bg-grayScale-800 dark:shadow-black/40">
          <Link
            href="/dashboard"
            onClick={() => setOpen(false)}
            className="block border-b border-grayScale-200 p-4 transition-colors hover:bg-grayScale-100 dark:border-grayScale-700 dark:hover:bg-grayScale-700 rounded-t-2xl"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-primary-main to-teal text-base font-bold text-white">
                {showDropdownImg ? (
                  <img src={user!.avatar_url!} alt="" className="h-full w-full object-cover" onError={() => setDropdownImgErr(true)} />
                ) : (
                  userInitial
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-grayScale-900 dark:text-white">
                  {displayName}
                </p>
                <p className="truncate text-xs text-grayScale-500">{userContact}</p>
              </div>
            </div>
          </Link>

          <div className="border-b border-grayScale-200 p-1.5 dark:border-grayScale-700">
            <Link
              href="/dashboard/wallet"
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-right text-sm text-grayScale-700 hover:bg-grayScale-100 dark:text-grayScale-300 dark:hover:bg-grayScale-700"
            >
              <Wallet size={16} className="text-secondary-main" />
              {t("common.userMenu.wallet", "کیف پول")}
              <span className="ms-auto text-xs font-bold text-grayScale-900 dark:text-white">
                {isPending
                  ? "…"
                  : isError
                    ? "—"
                    : `${balance.toLocaleString(locale === "fa" ? "fa-IR" : "en-US")} ${t("common.userMenu.toman", "ت")}`}
              </span>
            </Link>
          </div>

          <div className="p-1.5">
            {menuItems.map((it) => (
              <Link
                key={it.label}
                href={it.href}
                onClick={() => setOpen(false)}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-right text-sm text-grayScale-700 hover:bg-grayScale-100 dark:text-grayScale-300 dark:hover:bg-grayScale-700"
              >
                <it.icon size={16} />
                {it.label}
              </Link>
            ))}
          </div>

          <div className="border-t border-grayScale-200 p-1.5 dark:border-grayScale-700">
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-right text-sm font-medium text-alert-error-main hover:bg-alert-error-light/40 dark:hover:bg-alert-error-main/10"
            >
              <LogOut size={16} />
              {t("common.userMenu.logout", "خروج از حساب")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserMenu;
